import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Enhanced Supabase client with retry logic and better error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Retry configuration for token refresh
    retryAttempts: 3,
    retryDelay: 1000,
  },
  global: {
    headers: {
      'x-application-name': 'ctobra-client',
    },
  },
  db: {
    schema: 'public',
  },
  // Realtime configuration
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Retry helper function with exponential backoff
export const retryOperation = async (operation, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable (connection issues)
      const isRetryable = 
        error?.code === '57P01' || // Admin command termination
        error?.code === '57P03' || // Database not accepting connections
        error?.message?.includes('connection') ||
        error?.message?.includes('refresh token') ||
        error?.status === 503 || // Service unavailable
        error?.status === 504;   // Gateway timeout
      
      if (!isRetryable || attempt === maxRetries - 1) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms due to:`, error?.message);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Enhanced session recovery
export const recoverSession = async () => {
  try {
    const { data: { session }, error } = await retryOperation(
      () => supabase?.auth?.getSession()
    );
    
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Session recovery failed:', error?.message);
    // Clear invalid session
    await supabase?.auth?.signOut();
    return null;
  }
};

// Health check function
export const checkSupabaseHealth = async () => {
  try {
    const { error } = await supabase?.from('user_profiles')?.select('count')?.limit(1);
    return !error;
  } catch (error) {
    console.error('Supabase health check failed:', error?.message);
    return false;
  }
};
